import { useEffect, useRef, useState } from 'react';
import { pdfjsLib } from '../../lib/pdfWorker';
import ChangeBadge from './ChangeBadge';

const SCALE = 1.0;

export default function ComparePanel({
  file, side, label, pageCount,
  onPageVisible, onScrollPosition, scrollToPage, badges, activeChangeId,
  onCanvasReady,
}) {
  const containerRef = useRef(null);
  const [doc, setDoc] = useState(null);
  const [renderedPages, setRenderedPages] = useState(new Set());
  // Pages currently mid-render. The render effect re-runs every time
  // `renderedPages` updates (each completion bumps it); without this guard,
  // the same canvas would be handed a second `page.render()` call before
  // the first one resolves. pdfjs throws "Cannot use the same canvas during
  // multiple render() operations" on the second call — but its transform
  // state has already been mutated, leaving the canvas inverted. Track
  // in-flight pages in a ref so the guard doesn't trigger a re-render.
  const inFlightPagesRef = useRef(new Set());

  // Load the PDF on mount / file change.
  useEffect(() => {
    let cancelled = false;
    let docRef = null;
    // Reset render state for the new file so we don't keep stale entries.
    inFlightPagesRef.current = new Set();
    setRenderedPages(new Set());
    file.arrayBuffer().then((buf) =>
      pdfjsLib.getDocument({ data: buf.slice(0) }).promise
    ).then((d) => {
      if (cancelled) { d.destroy(); return; }
      docRef = d;
      setDoc(d);
    });
    return () => {
      cancelled = true;
      if (docRef) docRef.destroy();
    };
  }, [file]);

  // IntersectionObserver to fire onPageVisible
  useEffect(() => {
    if (!doc || !onPageVisible) return;
    const container = containerRef.current;
    if (!container) return;
    const observer = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          const page = Number(e.target.dataset.page);
          if (!Number.isNaN(page)) onPageVisible(page);
        }
      }
    }, { root: container, threshold: 0.2 });
    container.querySelectorAll('[data-page]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [doc, pageCount, onPageVisible]);

  // Scroll-to-page when prop changes
  useEffect(() => {
    if (scrollToPage == null) return;
    const el = containerRef.current?.querySelector(`[data-page="${scrollToPage}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [scrollToPage]);

  // Render each page into its canvas as it mounts.
  //
  // `pageCount` MUST be in the deps. PageSlots are mounted via
  // `Array.from({ length: pageCount })`, and `pageCount` is driven by the
  // CompareSession's `loaded` event (a separate code path from the local
  // doc load below). If `doc` settles BEFORE `pageCount` changes from 0
  // to N, this effect fires once, finds zero `[data-page]` slots in the
  // DOM (none mounted yet), and never runs again — leaving the canvases
  // blank when they do mount. Adding `pageCount` to deps re-fires the
  // effect once the slots exist. The in-flight + renderedPages guards
  // still ensure each canvas is rendered exactly once.
  useEffect(() => {
    if (!doc) return;
    const container = containerRef.current;
    if (!container) return;
    const slots = container.querySelectorAll('[data-page]');
    slots.forEach(async (slot) => {
      const canvas = slot.querySelector('canvas');
      if (!canvas) return;
      const pageIndex = Number(slot.dataset.page);
      if (renderedPages.has(pageIndex)) return;
      if (inFlightPagesRef.current.has(pageIndex)) return;
      if (pageIndex >= doc.numPages) return;
      inFlightPagesRef.current.add(pageIndex);
      try {
        const page = await doc.getPage(pageIndex + 1);
        const viewport = page.getViewport({ scale: SCALE });
        // Render at devicePixelRatio for crisp output on retina/HiDPI
        // displays. The canvas's intrinsic dimensions hold the high-res
        // bitmap; CSS dimensions stay at the PDF's native point size so
        // layout doesn't shift. Without this, the canvas is rendered at
        // 612×792 and the browser upscales it to fill the panel column,
        // producing visibly blurry output.
        const outputScale = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = Math.floor(viewport.width) + 'px';
        canvas.style.height = Math.floor(viewport.height) + 'px';
        const ctx = canvas.getContext('2d');
        const transform = outputScale !== 1
          ? [outputScale, 0, 0, outputScale, 0, 0]
          : null;
        await page.render({ canvasContext: ctx, viewport, transform }).promise;
        setRenderedPages((prev) => new Set(prev).add(pageIndex));
        // Surface the freshly-rendered canvas to the parent so the visual
        // diff can run on the main thread (pdfjs's page.render() needs
        // document.createElement for inline images — that throws in the
        // Compare worker. Doing pixelmatch here on the rendered bitmaps
        // sidesteps the issue entirely.)
        if (onCanvasReady) onCanvasReady(pageIndex, canvas);
      } finally {
        inFlightPagesRef.current.delete(pageIndex);
      }
    });
  }, [doc, renderedPages, pageCount]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col">
      <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 text-xs text-gray-500 uppercase tracking-wider font-semibold flex items-center justify-between">
        <span>{label} · {file?.name}</span>
        <span className="text-gray-400 normal-case font-normal">{doc?.numPages ?? '…'} pages</span>
      </div>
      <div
        ref={containerRef}
        className="flex-1 overflow-auto bg-gray-100"
        onScroll={(e) => {
          if (!onScrollPosition) return;
          // Find the topmost visible page
          const els = e.currentTarget.querySelectorAll('[data-page]');
          for (const el of els) {
            const rect = el.getBoundingClientRect();
            const containerTop = e.currentTarget.getBoundingClientRect().top;
            if (rect.bottom - containerTop > 20) {
              onScrollPosition(Number(el.dataset.page));
              break;
            }
          }
        }}
      >
        {Array.from({ length: pageCount ?? 0 }).map((_, i) => (
          <PageSlot key={i} page={i} hasPage={i < (doc?.numPages ?? 0)} badges={badges?.[i] || []} side={side} activeChangeId={activeChangeId} />
        ))}
      </div>
    </div>
  );
}

function PageSlot({ page, hasPage, badges, side, activeChangeId }) {
  if (!hasPage) {
    return (
      <div
        data-page={page}
        className="m-4 p-12 border-2 border-dashed border-gray-200 rounded-lg text-center text-gray-400 text-sm bg-white"
      >
        {side === 'left'
          ? 'No corresponding page in original'
          : 'Only in new version'}
        <div className="text-xs mt-1">Page {page + 1}</div>
      </div>
    );
  }
  return (
    <div data-page={page} className="m-4 relative inline-block bg-white shadow-sm">
      <canvas />
      {badges.map((b) => (
        <ChangeBadge key={b.id} badge={b} isActive={b.id === activeChangeId} />
      ))}
    </div>
  );
}
